import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartData } from '../types';

interface AnalyticsChartProps {
  timePeriod?: string;
  groupBy?: string;
  chartData?: ChartData[];
}

const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ 
  timePeriod = '7d', 
  groupBy = 'day',
  chartData = []
}) => {
  const [showVisitors, setShowVisitors] = useState(true);
  const [showSessions, setShowSessions] = useState(true);

  // Chart colors - using primary color and variations
  const chartColors = {
    pageViews: 'hsl(var(--primary))', // Primary color
    visitors: 'hsl(var(--primary) / 0.7)', // Primary with 70% opacity
    sessions: 'hsl(var(--primary) / 0.4)', // Primary with 40% opacity
  };



  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Page Views Over Time</CardTitle>
          <CardDescription>
            Track your website performance over the selected period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="text-center">
              <p className="text-muted-foreground">No data available for the selected period</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Page Views Over Time</CardTitle>
            <CardDescription>
              Track your website performance over the selected period
            </CardDescription>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'hsl(var(--primary))' }}></div>
              <label className="text-sm font-medium">Page Views</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="visitors" 
                checked={showVisitors} 
                onCheckedChange={(checked) => setShowVisitors(checked === true)}
              />
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'hsl(var(--primary) / 0.8)' }}></div>
              <label htmlFor="visitors" className="text-sm font-medium cursor-pointer">
                Visitors
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="sessions" 
                checked={showSessions} 
                onCheckedChange={(checked) => setShowSessions(checked === true)}
              />
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'hsl(var(--primary) / 0.6)' }}></div>
              <label htmlFor="sessions" className="text-sm font-medium cursor-pointer">
                Sessions
              </label>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" strokeOpacity={0.3} />
              <XAxis 
                dataKey="date" 
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '12px',
                  padding: '8px 12px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              {/* Page Views - Always visible */}
              <Area 
                type="monotone" 
                dataKey="pageViews" 
                stackId="1"
                stroke={chartColors.pageViews} 
                fill={chartColors.pageViews} 
                fillOpacity={0.6}
              />
              {/* Visitors - Conditional */}
              {showVisitors && (
                <Area 
                  type="monotone" 
                  dataKey="visitors" 
                  stackId="2"
                  stroke={chartColors.visitors} 
                  fill={chartColors.visitors} 
                  fillOpacity={0.6}
                />
              )}
              {/* Sessions - Conditional */}
              {showSessions && (
                <Area 
                  type="monotone" 
                  dataKey="sessions" 
                  stackId="3"
                  stroke={chartColors.sessions} 
                  fill={chartColors.sessions} 
                  fillOpacity={0.6}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalyticsChart;
